import React from 'react';
import { Helmet } from 'react-helmet-async';
import PublicLayout from '../components/PublicLayout';

const PrivacyPolicy: React.FC = () => {
  return (
    <PublicLayout>
      <Helmet>
        <title>Privacy Policy | Manatomy AI</title>
        <meta name="description" content="Privacy Policy for Manatomy AI - how we collect, use, and protect your data." />
      </Helmet>
      <div className="max-w-3xl mx-auto px-4 lg:px-6 py-12">


        <div className="prose prose-gray max-w-none space-y-6 text-secondary">
          <section>
            <h2 className="text-xl font-medium text-primary mb-3">AI Privacy Policy</h2>
            <ul className="text-sm mb-2">
              <li><strong>Effective Date:</strong> 12/12/2025</li>
              <li><strong>Website:</strong> <a href="https://ai.themanatomy.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">ai.themanatomy.com</a></li>
              <li><strong>Company:</strong> Manatomy</li>
            </ul>
            <h3 className="font-semibold text-primary mt-6 mb-2">1. Introduction</h3>
            <p>
              Manatomy (“Company”, “we”, “our”, “us”) respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, process, and protect your information when you use our AI-powered styling platform available at ai.themanatomy.com.<br />
              By using our AI platform and uploading your image, you consent to the practices described in this Policy.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">2. Information We Collect</h3>
            <ul>
              <li>
                <strong>A. Information You Provide</strong>
                <ul className="list-disc list-inside ml-4">
                  <li>Uploaded photographs (selfies)</li>
                  <li>Name, email address (if provided)</li>
                  <li>Product preferences</li>
                  <li>Communication details</li>
                </ul>
              </li>
              <li>
                <strong>B. Automatically Collected Information</strong>
                <ul className="list-disc list-inside ml-4">
                  <li>IP address</li>
                  <li>Device type and browser information</li>
                  <li>Usage behavior on the platform</li>
                  <li>Cookies and tracking data</li>
                </ul>
              </li>
              <li>
                <strong>C. AI-Derived Data</strong>
                <ul className="list-disc list-inside ml-4">
                  <li>Styling profile category (e.g., Warm / Cool / Neutral profile)</li>
                </ul>
                <span className="font-semibold text-red-600">We do <u>not</u> perform facial recognition or identity verification.</span>
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">3. Purpose of Processing</h3>
            <ul className="list-disc list-inside">
              <li>Generate personalized styling recommendations</li>
              <li>Match users with suitable Manatomy products</li>
              <li>Improve AI accuracy and platform functionality</li>
              <li>Provide customer support</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">4. Image Processing & Storage Policy</h3>
            <ul className="list-disc list-inside">
              <li>Images are processed solely for generating styling recommendations.</li>
              <li>Unless explicitly stated, uploaded images are not permanently stored.</li>
              <li>If temporarily stored for processing, images are deleted automatically after processing.</li>
              <li>We do not sell or commercially license user images.</li>
              <li>Images are not used for AI training without explicit consent.</li>
              <li>Third-party cloud providers are used; processing occurs under strict data protection agreements.</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">5. Legal Basis for Processing</h3>
            <p>
              Under applicable data protection laws (including India’s Digital Personal Data Protection Act, 2023), processing is based on:
            </p>
            <ul className="list-disc list-inside">
              <li>User consent</li>
              <li>Performance of service requested by user</li>
              <li>Legitimate business interests</li>
            </ul>
            <p>
              Users may withdraw consent at any time.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">6. Data Retention</h3>
            <ul className="list-disc list-inside">
              <li>AI-derived styling profiles may be stored for personalization.</li>
              <li>Uploaded images are deleted after processing unless otherwise specified.</li>
              <li>Account data is retained as long as necessary for service delivery or legal compliance.</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">7. Data Sharing</h3>
            <p>We do <u>not</u> sell personal data.</p>
            <p>We may share limited data with:</p>
            <ul className="list-disc list-inside">
              <li>Cloud hosting providers</li>
              <li>Analytics providers</li>
              <li>Legal authorities when required by law</li>
            </ul>
            <p>All third parties are contractually bound to protect your data.</p>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">8. User Rights</h3>
            <p>You have the right to:</p>
            <ul className="list-disc list-inside">
              <li>Request access to your data</li>
              <li>Request correction</li>
              <li>Request deletion</li>
              <li>Withdraw consent</li>
              <li>File a grievance</li>
            </ul>
            <p>
              To exercise your rights, email:{' '}
              <a href="mailto:help@themanatomy.com" className="text-primary hover:underline">help@themanatomy.com</a>
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">9. Data Security</h3>
            <p>We implement industry-standard technical and organizational measures including:</p>
            <ul className="list-disc list-inside">
              <li>Encrypted data transmission (HTTPS)</li>
              <li>Secure server infrastructure</li>
              <li>Restricted access controls</li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">10. Children’s Privacy</h3>
            <p>Our AI platform is not intended for individuals under 18 years of age.</p>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">11. International Data Transfers</h3>
            <p>
              If data is processed outside India, we ensure appropriate safeguards are in place.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">12. Policy Updates</h3>
            <p>
              We may update this Policy periodically. Continued use of the platform constitutes acceptance of updates.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-primary mb-2">Contact</h3>
            <p>
              For any privacy concerns, contact:&nbsp;
              <a href="mailto:help@themanatomy.com" className="text-primary hover:underline">help@themanatomy.com</a>
            </p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PrivacyPolicy;
